import { createSignal, createEffect, onCleanup, onMount } from "solid-js";
import cn from "classnames";
import { ImgixImage } from "#components/imgixImage";
import { CloseX, CaretLeft, CaretRight } from "#icons";

interface ImageData {
  alt: string;
  caption: string;
  credit: string;
  src: string;
  width: number;
  height: number;
  title: string;
}

interface FullScreenGalleryProps {
  images: ImageData[];
  handleExit: () => void;
  currentImageIndex?: number;
}

const getScrollPosition = (elem: HTMLUListElement) => {
  const { offsetWidth, scrollLeft, scrollWidth } = elem || {};
  const tail = scrollWidth - offsetWidth - scrollLeft;

  return {
    pageWidth: offsetWidth,
    head: scrollLeft,
    tail: Math.max(0, tail),
  };
};

export function FullScreenGallery({
  images,
  handleExit,
  currentImageIndex = 0,
}: FullScreenGalleryProps) {
  const [hasScroll, setHasScroll] = createSignal(false);
  const [galleryRef, setGalleryRef] = createSignal<HTMLUListElement>();
  const [activeChild, setActiveChild] = createSignal(currentImageIndex);
  const [scrollPosition, setScrollPosition] = createSignal({
    pageWidth: 0,
    head: 0,
    tail: 0,
  });

  console.log("GALLERY - activeChild():", activeChild());
  const isPrevDisabled = () => scrollPosition().head === 0;
  const isNextDisabled = () => scrollPosition().tail === 0;

  // Update scroll position and active child index
  const handleScrollUpdate = () => {
    const gallery = galleryRef();
    if (!gallery) return;

    const position = getScrollPosition(gallery);
    setScrollPosition(position);

    const { head, pageWidth } = position;
    if (pageWidth > 0) {
      setActiveChild(Math.round(head / pageWidth));
    }
  };

  const checkScroll = () => {
    const gallery = galleryRef();
    if (!gallery) return;

    const { scrollWidth, clientWidth } = gallery;
    setHasScroll(scrollWidth > clientWidth);
  };

  const handleScroll = (leftScroll: number) => {
    galleryRef()?.scrollTo({ left: leftScroll, behavior: "smooth" });
  };

  const handleNext = () => {
    const { head, tail, pageWidth } = getScrollPosition(galleryRef()!);
    const leftScrollDistance =
      tail >= pageWidth ? head + pageWidth : head + tail;
    handleScroll(leftScrollDistance);
    setActiveChild(prev => Math.min(prev + 1, images.length - 1));
  };

  const handlePrev = () => {
    const { head, pageWidth } = getScrollPosition(galleryRef()!);
    const leftScrollDistance = head >= pageWidth ? head - pageWidth : 0;
    handleScroll(leftScrollDistance);
    setActiveChild(prev => Math.max(prev - 1, 0));
  };

  const handleKeydown = (e: KeyboardEvent) => {
    switch (e.key) {
      case "ArrowLeft":
        handlePrev();
        break;
      case "ArrowRight":
        handleNext();
        break;
      case "Escape":
        handleExit();
        break;
      default:
        break;
    }
  };

  // Prevent page body from scrolling while the modal is open
  createEffect(() => {
    document.body.classList.add("overflow-y-hidden");
    onCleanup(() => document.body.classList.remove("overflow-y-hidden"));
  });

  // Add key listeners
  onMount(() => {
    checkScroll();
    handleScrollUpdate(); // Ensure scroll position is updated on mount
    window.addEventListener("keydown", handleKeydown);
    window.addEventListener("resize", checkScroll);

    const gallery = galleryRef();
    if (gallery) {
      gallery.addEventListener("scroll", handleScrollUpdate);
      // Scroll to the initial image
      const initialScrollLeft = currentImageIndex * gallery.offsetWidth;
      gallery.scrollTo({ left: initialScrollLeft, behavior: "smooth" });
    }

    return () => {
      window.removeEventListener("resize", checkScroll);
      onCleanup(() => {
        window.removeEventListener("keydown", handleKeydown);
      });
      if (gallery) {
        gallery.removeEventListener("scroll", handleScrollUpdate);
      }
    };
  });

  return (
    <div class="fixed top-0 left-0 w-full h-full z-50 bg-white">
      <div class="relative h-full">
        <button
          type="button"
          onClick={handleExit}
          class="bg-blue absolute z-10 top-4 right-4 text-white rounded-full"
        >
          <CloseX className="text-white m-2" />
        </button>
        <ul
          ref={setGalleryRef}
          class="flex overflow-x-auto snap-mandatory snap-x scroll-smooth no-scrollbar w-full h-3/4"
        >
          {images.map((image, i) => (
            <li class="w-full shrink-0 flex flex-col snap-start">
              <div class="bg-black relative w-full h-full flex items-center justify-center overflow-hidden">
                <ImgixImage
                  src={image.src}
                  alt={image.alt}
                  width={Math.min(image.width, 600)}
                  height={Math.min(image.height, 600)}
                  srcSet={`${image.src}?w=375 375w, ${image.src}?w=640 640w, ${image.src}?w=768 768w, ${image.src}?w=1024 1024w`}
                  imgixParams={{
                    w: "auto",
                    h: 600,
                    fit: "crop",
                    auto: "compress",
                  }}
                  sizes="(min-width: 1024px) 1024px, (min-width: 768px) 768px, (min-width: 640px) 640px, 375px"
                />
              </div>
            </li>
          ))}
        </ul>
        <div class="absolute bottom-0 bg-white left-0 w-full h-1/4 px-4">
          {images[activeChild()].caption && (
            <p class="mt-6 text-xs h-14">
              {images[activeChild()].caption.slice(0, 150)}
              {images[activeChild()].caption.length > 150 && "..."}
            </p>
          )}

          <div class={cn("w-40 mx-auto flex items-center text-blue mb-8")}>
            <button
              class={cn(
                isPrevDisabled()
                  ? "opacity-50 cursor-default"
                  : "cursor-pointer"
              )}
              onClick={handlePrev}
              disabled={isPrevDisabled()}
              aria-label="Previous slide"
              type="button"
            >
              <CaretLeft className="text-3xl" />
            </button>

            <div class="w-24 mx-auto text-center text-sm font-semibold text-black-300">
              {activeChild() + 1} / {images.length}
            </div>

            <button
              class={cn(
                isNextDisabled()
                  ? "opacity-50 cursor-default"
                  : "cursor-pointer"
              )}
              onClick={handleNext}
              disabled={isNextDisabled()}
              aria-label="Next slide"
              type="button"
            >
              <CaretRight className="text-3xl" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
